<script setup>
import Plotly from 'plotly.js-dist-min'
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  plotlyFigure: {
    type: Object,
    required: true,
  },
  query: {
    type: String,
    default: null,
  },
  messageId: {
    type: String,
    required: true,
  },
})

const { t } = useI18n()

const chartReference = ref(null)
const tableReference = ref(null)
const tableCsvData = ref('')
const hasTableData = ref(false)
const tableCopied = ref(false)

const DTYPE_MAP = {
  i2: Int16Array,
  i4: Int32Array,
  f4: Float32Array,
  f8: Float64Array,
}

const DEFAULT_CHART_CONFIG = {
  responsive: true,
  displayModeBar: true,
  displaylogo: false,
}

const DEFAULT_TABLE_CONFIG = {
  responsive: true,
  displayModeBar: false,
}

function decodeBase64Values(object) {
  if (!object?.bdata || !object?.dtype)
    return object

  try {
    const binaryString = atob(object.bdata)
    const bytes = new Uint8Array(binaryString.length)

    for (let index = 0; index < binaryString.length; index++) {
      bytes[index] = binaryString.charCodeAt(index)
    }

    const TypedArray = DTYPE_MAP[object.dtype]
    return TypedArray ? Array.from(new TypedArray(bytes.buffer)) : []
  }
  catch (error) {
    console.error('Error decoding base64 values:', error, object)
    return object
  }
}

function processTraceData(trace) {
  const processedTrace = { ...trace }

  const fieldsToProcess = ['x', 'y', 'z']

  fieldsToProcess.forEach((field) => {
    if (processedTrace[field]?.bdata) {
      processedTrace[field] = decodeBase64Values(processedTrace[field])
    }
  })

  return processedTrace
}

function renderChart(chartDiv, processedData) {
  if (!chartDiv)
    return

  const layout = {
    ...props.plotlyFigure.layout,
    autosize: true,
    margin: { l: 50, r: 50, t: 50, b: 50 },
  }

  Plotly.newPlot(chartDiv, processedData, layout, DEFAULT_CHART_CONFIG)
}

function escapeCsvValue(v) {
  const s = String(v ?? '')
  return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s
}

function buildTableCsv(processedData) {
  if (!processedData[0]?.x || !processedData[0]?.y)
    return ''
  const { x: xValues, y: yValues } = processedData[0]
  const xTitle = props.plotlyFigure?.layout?.xaxis?.title?.text || 'Category'
  const yTitle = props.plotlyFigure?.layout?.yaxis?.title?.text || 'Value'
  const header = [escapeCsvValue(xTitle), escapeCsvValue(yTitle)].join(',')
  const rows = Array.from({ length: Math.max(xValues?.length ?? 0, yValues?.length ?? 0) }, (_, index) =>
    [escapeCsvValue(xValues?.[index]), escapeCsvValue(yValues?.[index])].join(','))
  return [header, ...rows].join('\n')
}

function renderTable(tableDiv, processedData) {
  if (!tableDiv || !processedData[0]?.x || !processedData[0]?.y) {
    hasTableData.value = false
    tableCsvData.value = ''
    return
  }

  hasTableData.value = true
  tableCsvData.value = buildTableCsv(processedData)

  const { x: xValues, y: yValues } = processedData[0]
  const xTitle = props.plotlyFigure.layout?.xaxis?.title?.text || 'Category'
  const yTitle = props.plotlyFigure.layout?.yaxis?.title?.text || 'Value'

  const tableTrace = {
    type: 'table',
    header: {
      values: [[`<b>${xTitle}</b>`], [`<b>${yTitle}</b>`]],
      align: 'center',
      fill: { color: '#2a3f5f' },
      font: { family: 'Arial', size: 12, color: 'white' },
    },
    cells: {
      values: [xValues, yValues],
      align: ['left', 'center'],
      fill: { color: ['#f8f9fa'] },
      line: { color: '#dee2e6', width: 1 },
      font: { family: 'Arial', size: 11, color: '#333' },
    },
  }

  const tableLayout = {
    margin: { t: 0, l: 0, r: 0, b: 0 },
    height: Math.min(xValues.length * 30 + 60, 500),
    autosize: true,
  }

  Plotly.newPlot(tableDiv, [tableTrace], tableLayout, DEFAULT_TABLE_CONFIG)
}

function renderVisualization() {
  if (!props.plotlyFigure?.data) {
    console.warn('No plotly data found')
    hasTableData.value = false
    tableCsvData.value = ''
    return
  }

  try {
    const processedData = props.plotlyFigure.data.map(processTraceData)

    renderChart(chartReference.value, processedData)
    renderTable(tableReference.value, processedData)
  }
  catch (error) {
    console.error('Error rendering Plotly visualization:', error)
    hasTableData.value = false
    tableCsvData.value = ''
  }
}

function parseCsvLine(line) {
  const result = []
  let current = ''
  let inQuotes = false
  for (let index = 0; index < line.length; index++) {
    const char = line[index]
    if (char === '"') {
      if (inQuotes && line[index + 1] === '"') {
        current += '"'
        index++
      }
      else {
        inQuotes = !inQuotes
      }
    }
    else if (char === ',' && !inQuotes) {
      result.push(current)
      current = ''
    }
    else {
      current += char
    }
  }
  result.push(current)
  return result
}

function csvToRows(csv) {
  const lines = csv.split('\n').filter(Boolean)
  return lines.map(line => parseCsvLine(line))
}

function csvToHtmlTable(csv) {
  const rows = csvToRows(csv)
  if (!rows.length)
    return ''
  const thead = rows[0].map(cell => `<th>${escapeHtml(cell)}</th>`).join('')
  const headerRow = `<tr>${thead}</tr>`
  const bodyRows = rows.slice(1).map(row =>
    `<tr>${row.map(cell => `<td>${escapeHtml(cell)}</td>`).join('')}</tr>`,
  ).join('')
  return `<table border="1" cellpadding="4" cellspacing="0"><thead>${headerRow}</thead><tbody>${bodyRows}</tbody></table>`
}

function escapeHtml(text) {
  const s = String(text)
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function csvToTsv(csv) {
  const rows = csvToRows(csv)
  return rows.map(row => row.join('\t')).join('\n')
}

async function handleCopyPlotlyTable() {
  if (!tableCsvData.value)
    return
  tableCopied.value = true
  setTimeout(() => {
    tableCopied.value = false
  }, 2000)
  if (!navigator.clipboard)
    return
  const tsv = csvToTsv(tableCsvData.value)
  const html = csvToHtmlTable(tableCsvData.value)
  try {
    const htmlBlob = new Blob([html], { type: 'text/html' })
    const plainBlob = new Blob([tsv], { type: 'text/plain' })
    await navigator.clipboard.write([
      new ClipboardItem({ 'text/html': htmlBlob, 'text/plain': plainBlob }),
    ])
  }
  catch {
    await navigator.clipboard.writeText(tsv)
  }
}

function handleExportPlotlyTable() {
  if (!tableCsvData.value)
    return
  const blob = new Blob([tableCsvData.value], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `bradley-genie-table-${props.messageId || Date.now()}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

onMounted(() => {
  renderVisualization()
})

watch(
  () => [props.plotlyFigure?.data, props.query],
  renderVisualization,
)

onBeforeUnmount(() => {
  if (chartReference.value) {
    Plotly.purge(chartReference.value)
  }
  if (tableReference.value) {
    Plotly.purge(tableReference.value)
  }
})
</script>

<template>
  <div class="plotly-visualization">
    <div
      :id="`plotly-chart-${messageId}`"
      ref="chartReference"
      class="plotly-visualization__chart"
    />

    <div
      :id="`plotly-table-${messageId}`"
      ref="tableReference"
      class="plotly-visualization__table"
    />

    <div
      v-if="hasTableData"
      class="flex items-center gap-4 mt-2 mb-2"
    >
      <button
        type="button"
        class="plotly-visualization__table-action text-dss-secondary-650 hover:text-dss-secondary-400"
        :title="t('newBradley.chatFile.exportToSheet')"
        @click="handleExportPlotlyTable"
      >
        <i class="pi pi-file-export" />
        {{ t('newBradley.chatFile.exportToSheet') }}
      </button>
      <button
        type="button"
        class="plotly-visualization__table-action text-dss-secondary-650 hover:text-dss-secondary-400"
        :title="tableCopied ? t('newBradley.chatFile.copied') : t('newBradley.chatFile.copyTable')"
        @click="handleCopyPlotlyTable"
      >
        <i class="pi pi-clone" />
        {{ tableCopied ? t('newBradley.chatFile.copied') : t('newBradley.chatFile.copyTable') }}
      </button>
    </div>

    <div
      v-if="query"
      class="plotly-visualization__sql"
    >
      <pre><code>{{ query }}</code></pre>
    </div>
  </div>
</template>

<style scoped>
.plotly-visualization {
  width: 100%;
  margin: 1rem 0;
}

.plotly-visualization__chart {
  width: 100%;
  min-height: 400px;
  margin-bottom: 1.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  padding: 10px;
  overflow: hidden;
  max-width: 100%;
}

.plotly-visualization__table {
  width: 100%;
  margin-bottom: 1.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
  max-width: 100%;
}

.plotly-visualization__sql {
  background-color: #282c34;
  color: #abb2bf;
  padding: 15px;
  border-radius: 6px;
  overflow-x: auto;
  margin-top: 1rem;
}

.plotly-visualization__sql pre {
  margin: 0;
}

.plotly-visualization__sql code {
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.plotly-visualization__table-action {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0;
  font-size: 0.875rem;
  font-weight: 500;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: color 0.2s ease;
}

.plotly-visualization__table-action i {
  color: inherit;
}
</style>
